import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import VolumeSlider from "../../ui/AudioVolumeSlider";
import { useAudioEditor } from "../../../context/AudioEditorContext";
import Button from "../../ui/Button";
import useAudioSpeedProcessor from "./useAudioSpeedProcessor";
import HorizontalSeparator from "../HorizontalSeparator";
import { Tooltip } from "@mui/material";
import useAudioEditorNotifications from "./hooks/useAudioEditorNotifications";
import Icon from "../Icon";
import styles from "./AudioEditorSidebar.module.css";

const AudioEditorSidebar = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { notifyError, notifySuccess } = useAudioEditorNotifications();

    const { selectedTrack, setTrackVolume, getTrackVolume, processTrackSpeed } = useAudioEditor();

    useAudioSpeedProcessor();

    const [speedValue, setSpeedValue] = useState<number>(1);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    // If no track is selected, show a placeholder
    if (!selectedTrack) {
        return (
            <div className={styles.sidebar}>
                <h1 className={styles.title}>
                    <Icon name="ic-settings" />
                    {t("settings")}
                </h1>
                <div className={styles.emptyStateContainer}>
                    <h3 className={styles.emptyStateTitle}>{t("noTrackSelected")}</h3>
                    <p className={styles.emptyStateText}>{t("selectOrImportAudio")}</p>
                </div>
            </div>
        );
    }

    const initialVolume = getTrackVolume?.(selectedTrack.index) ?? 0;

    const handleProcessSpeed = async () => {
        if (processTrackSpeed && selectedTrack) {
            setIsProcessing(true);
            try {
                await processTrackSpeed(selectedTrack.index, speedValue);
                notifySuccess(t("speedChangeApplied"));
            } catch (error) {
                notifyError(error, t("errorProcessingTrackSpeed"));
            } finally {
                setSpeedValue(1.0);
                setIsProcessing(false);
            }
        }
    };

    return (
        <div className={styles.sidebar}>
            <h1 className={styles.title}>
                <Icon name="ic-settings" />
                {t("settings")}
            </h1>
            <h3 className={styles.categoryTitle}>{t("contentInformation")}</h3>

            <div className={styles.controlSection}>
                <h4 className={styles.controlLabel}>{t("volume")}</h4>
                {setTrackVolume && (
                    <VolumeSlider
                        key={`volume-${selectedTrack.id}`}
                        initialValue={initialVolume}
                        min={-100}
                        max={100}
                        step={1}
                        onChange={setTrackVolume}
                        width="auto"
                        hideLabels
                    />
                )}
            </div>
            <HorizontalSeparator />
            <div className={styles.controlSection}>
                <h4 className={styles.controlLabel}>{t("speed")}</h4>
                <VolumeSlider
                    key={`speed-${selectedTrack.id}`}
                    initialValue={speedValue}
                    min={0.8}
                    max={1.2}
                    step={0.1}
                    onChange={setSpeedValue}
                    width="auto"
                    hideLabels
                    fractionDigits={1}
                />
                <Tooltip
                    title={t("changeSpeedFadeWarning")}
                    arrow
                    disableHoverListener={isProcessing || speedValue === 1}
                    disableFocusListener={isProcessing || speedValue === 1}
                    disableTouchListener={isProcessing || speedValue === 1}
                >
                    <span>
                        <Button
                            onClick={handleProcessSpeed}
                            disabled={isProcessing || speedValue === 1}
                            variant="primary"
                            shape="default"
                            block
                            className={styles.applySpeedButton}
                            label={isProcessing ? t("processing") : t("applySpeed")}
                            icon={!isProcessing ? <Icon name="ic-gauge" className="ic" /> : undefined}
                        />
                    </span>
                </Tooltip>
            </div>
        </div>
    );
};

export default AudioEditorSidebar;
